import React from "react";
import ReactDOM from "react-dom/client";
import * as Sentry from "@sentry/react";
import "@/index.css";
import App from "@/App";
import { initAnalytics } from "@/lib/analytics";

if (process.env.REACT_APP_SENTRY_DSN) {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    sendDefaultPii: false,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: false, blockAllMedia: false }),
    ],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0.05,
    replaysOnErrorSampleRate: 1.0,
    environment: process.env.NODE_ENV,
  });
}

// Patch ResizeObserver to debounce callbacks inside rAF — this avoids the
// "ResizeObserver loop completed with undelivered notifications" error that
// triggers CRA's dev error overlay (harmless warning from Recharts/etc.).
if (typeof window !== "undefined" && window.ResizeObserver) {
  const _RO = window.ResizeObserver;
  window.ResizeObserver = class extends _RO {
    constructor(callback) {
      super((entries, observer) => {
        if (typeof window.requestAnimationFrame === "function") {
          window.requestAnimationFrame(() => { try { callback(entries, observer); } catch (e) { /* swallow */ } });
        } else {
          try { callback(entries, observer); } catch (e) { /* swallow */ }
        }
      });
    }
  };
}

const RESIZE_OBSERVER_MSG = "ResizeObserver loop";
window.addEventListener("error", (e) => {
  if (e?.message?.includes(RESIZE_OBSERVER_MSG)) {
    e.stopImmediatePropagation();
    e.preventDefault();
  }
}, true);
window.addEventListener("unhandledrejection", (e) => {
  if (typeof e?.reason?.message === "string" && e.reason.message.includes(RESIZE_OBSERVER_MSG)) {
    e.preventDefault();
  }
});

initAnalytics();

if (process.env.NODE_ENV === "development") {
  import("eruda").then((m) => m.default.init());
}

// PWA: solo en producción — en dev interferiría con el hot reload de CRA.
if (process.env.NODE_ENV === "production" && "serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch((e) => {
      Sentry.captureException(e);
    });
  });
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
